import { type ReactElement } from 'react';
import { Navigate, Outlet, redirect, useLocation, useSearchParams } from 'react-router';
import { SIGNED_IN_LANDING } from './landing';
import { bootstrapOpen, loadAvailability, signUpOpen } from './registration-availability';
import { hasSession, isAdmin } from './session.store';

/**
 * 034 T019 — Angular's `CanActivateFn` guards, as route elements.
 *
 * **The idiom that must survive is guard-on-the-parent.** In `app.routes.ts` the session guard sits
 * on the shell route, so every child inherits it and "protecting a new feature" is not a step
 * someone can forget. React Router reproduces that exactly: these render an `<Outlet />`, so a
 * route nested under one cannot opt out of it. A per-screen `useRequireSession()` hook would have
 * been the idiomatic-looking translation and would have demoted a structural guarantee to a habit.
 *
 * Nothing here awaits the session: it is already settled, because startup restores it before the
 * first navigation. That is what removes the sign-in/guard redirect loop, rather than each guard
 * remembering to wait.
 */

/**
 * Protected routes (005 FR-009). Nothing but a predicate.
 *
 * **The wanted URL goes in a `returnUrl` QUERY PARAM, in the address bar**, exactly as Angular's
 * `createUrlTree(['/sign-in'], { queryParams: { returnUrl: state.url } })` put it. The port moved it
 * into the navigation's `state` instead, on the reasoning that a redirect can carry it there without
 * showing it — and that broke the feature outright, because `sign-in-page.tsx` reads it back from
 * `useSearchParams` and nothing ever wrote it there. The two halves disagreed silently: the address
 * bar showed a bare `/sign-in`, signing in landed on the home page, and 005 SC-003's whole chain —
 * "protected → sign-in → the route you asked for" — took you somewhere else.
 *
 * The address bar is also the right place for it on its own merits. Router state does not survive a
 * reload or a pasted link, and this is a URL a practitioner can land on with neither: it is where an
 * expired session drops you mid-task.
 *
 * 036 (issue 219): reads `hasSession`, not `isSignedIn` — an `unconfirmed` practitioner (a stored
 * token and identity that pair, but the server could not be asked) is exactly as entitled to see
 * their own screen as a signed-in one; that is the whole point of the fourth status. Nothing here
 * SENDS anything, so admitting `unconfirmed` is safe in a way it would not be for the outbox or the
 * renewing client, both of which stay on `isSignedIn`.
 */
export function RequireSignedIn(): ReactElement {
  const admitted = hasSession.use();
  const location = useLocation();
  if (admitted) return <Outlet />;
  const wanted = encodeURIComponent(location.pathname + location.search);
  return <Navigate to={`/sign-in?returnUrl=${wanted}`} replace />;
}

/**
 * Sign-in itself: a practitioner with a session has no business here.
 *
 * 036 (issue 219): `hasSession`, for the same reason as `RequireSignedIn` above — an `unconfirmed`
 * practitioner already has a session (the one last confirmed) and gains nothing from a sign-in form
 * that cannot presently be answered either.
 */
export function RequireAnonymous(): ReactElement {
  return hasSession.use() ? <Navigate to={SIGNED_IN_LANDING} replace /> : <Outlet />;
}

/**
 * The two token-bearing link surfaces (018): `/password-reset?token=…` and `/verify-email?token=…`.
 *
 * A link out of an email is followed in whatever browser the mail was read in, and for email
 * verification the *dominant* case is a signed-in one — the practitioner sets their address on
 * `/account`, then clicks the link in the same session. Behind the anonymous rule alone that click
 * redirects to the landing page and the token is never confirmed, so the one flow the feature exists
 * for is the one flow that cannot complete. `/password-reset` has the same shape for anyone signed in
 * as a different account.
 *
 * So: a token in the URL admits the page unconditionally — the token is the credential, and who
 * happens to hold a session is beside the point. WITHOUT a token the page is `/password-reset`'s
 * request form, which a signed-in practitioner genuinely has no business on, and there the anonymous
 * rule still applies.
 */
export function RequireLinkToken(): ReactElement {
  const [params] = useSearchParams();
  // 036 (issue 219): `hasSession`, consistently with the two guards above — an `unconfirmed`
  // practitioner is treated as having a session for every routing decision, never for sending.
  const admitted = hasSession.use();
  if (params.get('token')) return <Outlet />;
  return admitted ? <Navigate to={SIGNED_IN_LANDING} replace /> : <Outlet />;
}

/**
 * Administration screens (014 FR-016). This decides what to *show*, never what to *permit* (R-013):
 * it reads the cached account's standing — possibly minutes stale — and the routes it fronts all
 * re-read standing server-side, so a demoted holder who still sees the menu gets a 403 from the
 * first request. A non-administrator lands on the landing screen, not sign-in: their session is
 * fine, this screen just is not theirs.
 */
export function RequireAdmin(): ReactElement {
  return isAdmin.use() ? <Outlet /> : <Navigate to={SIGNED_IN_LANDING} replace />;
}

/**
 * `/sign-up` exists only while a door does (014 FR-003). A LOADER, not a component, and that is the
 * whole point: Angular's `registrationOpenGuard` was async and was AWAITED BEFORE ACTIVATION, so the
 * screen never rendered until the answer was in. React Router's loader is the real counterpart.
 *
 * The component form is what this port reached for first, and it was wrong in a way that only showed
 * up under a live API: it flipped local state after the read resolved and then rendered `<Outlet />`
 * whose child route was still loading its module. React treats a suspension during that synchronous
 * update as error 311, and React Router replaces the page with its error screen — which reads as a
 * routing bug rather than as a guard rendering too early. Three speculative fixes missed it because
 * every configuration without a live API never reaches the path.
 *
 * A closed instance's visitor is returned to sign-in with no returnUrl and no reason: the absence of
 * a front door is not explained. A failed read answers closed, which the store already guarantees
 * (`loadAvailability()` never rejects).
 */
export async function registrationOpenLoader(): Promise<Response | null> {
  await loadAvailability();
  return bootstrapOpen.get() || signUpOpen.get() ? null : redirect('/sign-in');
}
